import type { ActionContext, ActionDependencies } from '@/services/action/types.ts'

function normalizeComparableValue(value: any): any {
  if (Array.isArray(value)) {
    return value.map((item) => normalizeComparableValue(item))
  }

  if (value && typeof value === 'object') {
    const source = '_rowData' in value && value._rowData != null ? value._rowData : value
    const normalized: Record<string, any> = {}

    Object.keys(source)
      .sort()
      .forEach((key) => {
        const nextValue = source[key]
        if (nextValue !== undefined) {
          normalized[key] = normalizeComparableValue(nextValue)
        }
      })

    return normalized
  }

  return value
}

function sameFieldPayload(left: any, right: any): boolean {
  try {
    return (
      JSON.stringify(normalizeComparableValue(left)) ===
      JSON.stringify(normalizeComparableValue(right))
    )
  } catch {
    return false
  }
}


export function extractSerializedFieldShape(field: unknown): unknown {
  if (field != null && typeof field === 'object' && !Array.isArray(field) && 'value' in field) {
    return (field as { value: unknown }).value
  }
  return field
}

/**
 * Плоские значения родительских uniData полей 
 */
export function uniFieldsKeysToSerializedComparable(
  uniFields: Record<string, unknown> | null | undefined,
  keysFromAfterRun: Iterable<string>,
): Record<string, unknown> {
  const src = uniFields && typeof uniFields === 'object' ? uniFields : {}
  const out: Record<string, unknown> = {}
  for (const k of keysFromAfterRun) {
    if (!Object.prototype.hasOwnProperty.call(src, k)) {
      continue
    }
    out[k] = extractSerializedFieldShape((src as Record<string, unknown>)[k])
  }
  return out
}

export function isDialogCloseParentMergeBlockedKey(fieldKey: string): boolean {
  return /^(recepients|recipient|recipients)$/i.test(String(fieldKey).trim())
}

export function pickSerializedFieldChanges(
  before: Record<string, unknown> | null | undefined,
  after: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  const b =
    before && typeof before === 'object' && !Array.isArray(before)
      ? (before as Record<string, unknown>)
      : {}
  const a =
    after && typeof after === 'object' && !Array.isArray(after)
      ? (after as Record<string, unknown>)
      : {}
  const delta: Record<string, unknown> = {}
  for (const k of Object.keys(a)) {
    if (!Object.prototype.hasOwnProperty.call(b, k) || !sameFieldPayload(b[k], a[k])) {
      delta[k] = a[k]
    }
  }
  return delta
}

function normalizeRowCollection(rows: any[]): any[] {
  return rows.map((row) => normalizeComparableValue(row))
}

export function sameRows(left: any[] | undefined, right: any[] | undefined): boolean {
  if (!Array.isArray(left) || !Array.isArray(right)) return false
  if (left.length !== right.length) return false
  try {
    return JSON.stringify(normalizeRowCollection(left)) === JSON.stringify(normalizeRowCollection(right))
  } catch {
    return false
  }
}

/**
 * TODO: скорее всего лишнее, вообще не нужное. Проверить. Возможно вынести в утилиты
 *
 * @param changedData
 * @param context
 * @private
 */
export function stripInitialActionPayloadFields(changedData: any, context: ActionContext): any {
  if (!changedData || typeof changedData !== 'object') {
    return changedData
  }

  const initialClientData = (context as any).__initialClientData
  const initialFields = initialClientData?.fields?.fields || initialClientData?.fields || {}
  if (!initialFields || typeof initialFields !== 'object' || Array.isArray(initialFields)) {
    return changedData
  }

  const fields = changedData.fields?.fields || changedData.fields
  if (!fields || typeof fields !== 'object' || Array.isArray(fields)) {
    return changedData
  }

  const nextFields = { ...fields }
  let hasRemovedInitialFields = false
  const selectionPayloadFieldNames = new Set<string>(
    Array.isArray((context as any).__selectionPayloadFieldNames)
      ? (context as any).__selectionPayloadFieldNames
      : [],
  )

  Object.entries(fields).forEach(([fieldName, fieldData]) => {
    if (selectionPayloadFieldNames.has(fieldName)) {
      delete nextFields[fieldName]
      hasRemovedInitialFields = true
      return
    }

    if (Object.prototype.hasOwnProperty.call(initialFields, fieldName)) {
      const initialFieldData = initialFields[fieldName]
      if (sameFieldPayload(fieldData, initialFieldData)) {
        delete nextFields[fieldName]
        hasRemovedInitialFields = true
      }
    }
  })

  if (!hasRemovedInitialFields) {
    return changedData
  }

  if (changedData.fields?.fields) {
    return {
      ...changedData,
      fields: {
        ...changedData.fields,
        fields: nextFields,
      },
    }
  }

  return {
    ...changedData,
    fields: nextFields,
  }
}

export function sanitizeCheckboxSelectionChangedData(
  changedData: any,
  context: ActionContext,
  dependencies?: ActionDependencies,
): any {
  if (!changedData || typeof changedData !== 'object') {
    return changedData
  }

  const tableSelections = getKnownTableSelections(context, dependencies)
  if (tableSelections.length === 0) {
    return changedData
  }

  const fields = changedData.fields?.fields || changedData.fields
  if (!fields || typeof fields !== 'object' || Array.isArray(fields)) {
    return changedData
  }

  const nextFields = { ...fields }
  let hasRemovedSelectionPayload = false

  Object.entries(fields).forEach(([fieldName, fieldData]: [string, any]) => {
    if (isKnownSelectionPayload(fieldName, fieldData, tableSelections)) {
      delete nextFields[fieldName]
      hasRemovedSelectionPayload = true
    }
  })

  if (!hasRemovedSelectionPayload) {
    return changedData
  }

  if (changedData.fields?.fields) {
    return {
      ...changedData,
      fields: {
        ...changedData.fields,
        fields: nextFields,
      },
    }
  }

  return {
    ...changedData,
    fields: nextFields,
  }
}

export function getKnownTableSelections(
  context: ActionContext,
  dependencies?: ActionDependencies,
): Array<{ fieldName: string; rows: any[] }> {
  const selections: Array<{ fieldName: string; rows: any[] }> = []
  const pushSelection = (selection: { fieldName: string; rows: any[] } | null | undefined) => {
    if (!selection?.fieldName) {
      return
    }

    const alreadyAdded = selections.some(
      (existing) =>
        existing.fieldName === selection.fieldName && sameRows(existing.rows, selection.rows),
    )
    if (!alreadyAdded) {
      selections.push({
        fieldName: selection.fieldName,
        rows: Array.isArray(selection.rows) ? selection.rows : [],
      })
    }
  }

  pushSelection(
    dependencies?.getTableSelection?.(context.formId, context.panelId, context.elementId),
  )

  let groupSelections =
    dependencies?.getTableSelections?.(context.formId, context.panelId) ||
    dependencies?.formProvider?.getTableSelections?.(context.formId, context.panelId) ||
    []

  if (
    (!Array.isArray(groupSelections) || groupSelections.length === 0) &&
    context.panelId != null
  ) {
    groupSelections =
      dependencies?.getTableSelections?.(context.formId) ||
      dependencies?.formProvider?.getTableSelections?.(context.formId) ||
      []
  }

  if (Array.isArray(groupSelections)) {
    groupSelections.forEach(pushSelection)
  }

  return selections
}

export function isKnownSelectionPayload(
  fieldName: string,
  fieldData: any,
  selections: Array<{ fieldName: string; rows: any[] }>,
): boolean {
  if (!fieldData || typeof fieldData !== 'object' || fieldData.type !== 'UniDataTable') {
    return false
  }

  return selections.some(
    (selection) =>
      selection.fieldName === fieldName && sameRows(fieldData?.value?.rows, selection.rows),
  )
}
